import { OrderDrawerIdentifier } from "@features/orders-features/components/order-drawer/types";
import { useGetShippingDetails } from "@features/orders-features/hooks/useGetShippingDetails";
import { useClaims } from "../../../../../../../auth/ClaimsProvider";
import {
  Card,
  CardActionArea,
  Collapse,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useId, useMemo, useState } from "react";
import {
  ApartmentOutlined,
  LocationOn,
  Person,
  StickyNote2Outlined,
} from "@mui/icons-material";
import { ShipTo } from "@features/orders-features/types/types";
import { KeyValue } from "@features/orders-features/types/commonTypes";

type ShippingContentProps = {
  deliveryContact?: string;
  recipient?: string;
  address: Array<KeyValue>;
  memo: string[];
};

export function ShippingContent({
  deliveryContact,
  recipient,
  address,
  memo,
}: ShippingContentProps) {
  return (
    <Stack direction="column" spacing={1} sx={{ px: 2, py: 2 }}>
      {deliveryContact && (
        <Typography
          variant="body1"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Tooltip title="Delivery contact">
            <Person fontSize="small" color="action" sx={{ mr: 1 }} />
          </Tooltip>
          {deliveryContact}
        </Typography>
      )}
      {recipient && (
        <Typography
          variant="body1"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Tooltip title="Company">
            <ApartmentOutlined fontSize="small" color="action" sx={{ mr: 1 }} />
          </Tooltip>
          {recipient}
        </Typography>
      )}
      {address.map((line, index) => (
        <Typography
          key={line.key}
          variant="body1"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Tooltip title="Delivery address">
            <LocationOn
              fontSize="small"
              color="action"
              sx={{ mr: 1, visibility: index === 0 ? "visible" : "hidden" }}
            />
          </Tooltip>
          {line.value}
        </Typography>
      ))}
      {memo.length > 0 && (
        <Typography
          variant="body1"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Tooltip title="Delivery memo">
            <StickyNote2Outlined fontSize="small" color="action" sx={{ mr: 1 }} />
          </Tooltip>
          {memo.join(" / ")}
        </Typography>
      )}
    </Stack>
  );
}

export function OrderDrawerShippingDetails({
  id,
  shipTo,
  disableGutters = false,
}: {
  id: OrderDrawerIdentifier;
  shipTo: ShipTo;
  disableGutters?: boolean;
}) {
  const { getClaimValue } = useClaims();
  const [expanded, setExpanded] = useState(true);
  const shippingSectionId = useId();
  const shippingPanelId = useId();

  const orgUnitIdCurrent = Number(getClaimValue("orgUnitIdCurrent") ?? null);
  const { biOrderId, webOrderId, orgUnitId } = id;

  const { data } = useGetShippingDetails(
    biOrderId ?? undefined,
    webOrderId ?? undefined,
    orgUnitId ?? orgUnitIdCurrent,
  );

  const shipping = useMemo(() => {
    const lines: Array<KeyValue> = [];
    const notes: string[] = [];

    const p = data;
    const s = p?.shipTo;

    if (!s)
      return {
        deliveryContact: shipTo.contact || undefined,
        recipient: shipTo.recipient || undefined,
        address: shipTo.address?.length
          ? shipTo.address.map((value, index) => ({
              key: `shipTo-${index}`,
              value,
            }))
          : lines,
        memo: notes,
      };

    const deliveryContact = p?.delivery?.contact || undefined;
    const recipient = s.addressName || undefined;

    if (p?.delivery?.memo) {
      if (s?.attention && !p.delivery.memo.includes(s.attention)) {
        notes.push(`Attn: ${s.attention}`);
      }
      notes.push(p.delivery.memo);
    } else if (s?.attention) {
      notes.push(`Attn: ${s.attention}`);
    }

    if (s.addressLine1) lines.push({key: "addr1", value: s.addressLine1});
    if (s.addressLine2) lines.push({key: "addr2", value: s.addressLine2});
    if (s.addressLine3) lines.push({key: "addr3", value: s.addressLine3});

    if (s.county) lines.push({key: "county", value: s.county});

    const cityState = [s.city, s.stateProvince].filter(Boolean).join(", ");
    const cityPostal = [cityState, s.postalCode]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (cityPostal) lines.push({key: "cityStateZip", value: cityPostal});

    if (s.countryCode) lines.push({key: "country", value: s.countryCode});
    else if (s.country) lines.push({key: "country", value: s.country});

    return {
      deliveryContact,
      recipient,
      address: lines,
      memo: notes,
    };
  }, [data, shipTo]);

  if (
    !shipping.deliveryContact &&
    !shipping.recipient &&
    !shipping.address.length &&
    !shipping.memo.length
  ) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ mx: disableGutters ? 0 : 2, my: 0, borderRadius: 1 }}>
      <CardActionArea
        id={shippingSectionId}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={shippingPanelId}
        aria-label="Toggle shipping details"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 2,
        }}
      >
        <Typography
          component="h3"
          variant="subtitle1"
          sx={{ textTransform: "uppercase", fontWeight: "normal" }}
        >
          Ship To
        </Typography>
        <ExpandMoreIcon
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            color: "action.active",
          }}
          aria-hidden="true"
        />
      </CardActionArea>
      <Collapse
        id={shippingPanelId}
        in={expanded}
        role="region"
        aria-labelledby={shippingSectionId}
      >
        <Divider sx={{ borderStyle: "solid" }} />
        <ShippingContent {...shipping} />
      </Collapse>
    </Card>
  );
}
